import { useState } from 'react';
import type { Deck } from './types';
import { isDeckPublic } from '@/registry';

function buildShareUrl(deck: Deck): string {
  if (typeof window === 'undefined') return `/${deck.slug}`;
  return `${window.location.origin}/${deck.slug}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy fallback
    }
  }
  if (typeof document === 'undefined') return false;
  const input = document.createElement('input');
  input.value = text;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(input);
  return ok;
}

export function ShareControls({ deck }: { readonly deck: Deck }) {
  const [copied, setCopied] = useState(false);

  if (!isDeckPublic(deck)) return null;

  const onCopy = async () => {
    const ok = await copyToClipboard(buildShareUrl(deck));
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="hud-share" role="group" aria-label="Share this deck">
      <span className="hud-share-badge">Public</span>
      <button type="button" className="hud-share-copy" onClick={onCopy}>
        {copied ? 'Copied' : 'Copy share link'}
      </button>
    </div>
  );
}
